import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { budgetsService } from '../services/supabase';
import { useTrips } from './TripsContext';
import { tripsService } from '../services/supabase';

type BudgetStats = {
  totalAllocations?: number;
  totalAdjustments?: number;
  currentBudget?: number;
  totalExpenses?: number;
  remainingBudget?: number;
};

interface BudgetContextType {
  loaded: boolean;

  budgetCategories: any[];
  budgetStats: BudgetStats;

  refresh: (tripId: string) => Promise<void>;
}

const BudgetContext = createContext<BudgetContextType | undefined>(undefined);

interface BudgetProviderProps {
  children: React.ReactNode;
}

export const BudgetProvider: React.FC<BudgetProviderProps> = ({ children }) => {
  const [loaded, setLoaded] = useState(false);
  const [budgetCategories, setBudgetCategories] = useState<any[]>([]);
  const [budgetStats, setBudgetStats] = useState<BudgetStats>({});

  const { currentTrip } = useTrips();

  const load = useCallback(async (tripId: string) => {
    console.log("Loading budget data for trip", tripId);
    try {
      setLoaded(false);

      // Get budget categories
      const categories = await budgetsService.getTripBudgetCategories(tripId);
      setBudgetCategories(categories);

      // Get trip dashboard data for stats
      const dashboard = await tripsService.getTripDashboard(tripId);

      if (dashboard) {
        setBudgetStats({
          currentBudget: dashboard.total_budget,
          totalExpenses: dashboard.total_expenses,
          remainingBudget: dashboard.remaining_budget,
        });
      }

      setLoaded(true);
    } catch (error) {
      console.error('Error loading budget data:', error);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!currentTrip?.id) return;
    load(currentTrip.id);
  }, [currentTrip?.id, load]);

  const refresh = useCallback(async (tripId: string) => {
    await load(tripId);
  }, [load]);

  const contextValue = {
    loaded,
    budgetCategories,
    budgetStats,
    refresh,
  };

  return <BudgetContext.Provider value={contextValue}>{children}</BudgetContext.Provider>;
};


export const useBudget = () => {
  const context = useContext(BudgetContext);
  
  if (context === undefined) {
    throw new Error('useBudget must be used within a BudgetProvider');
  }

  return context;
};

